import { Flex } from '@tremor/react';
import { NavbarLayout } from './NavbarLayout';
import { ChangeEntityButton } from '../Buttons/ChangeEntityButton/ChangeEntityButton';
import { ChevronDown, HandCoins, LogOut, Mail, User } from 'lucide-react';
import { useState } from 'react';
import { auth, generateCustomerPortalLinkForUserCloudFunction } from '@/firebase/init';
import clsx from 'clsx';
import { useRevalidator } from 'react-router-dom';
import { logout } from '@/utils/logout';
import { UserProfileModal } from '../Modals/UserProfileModal/UserProfileModal';

interface Props {
  readonly invitationsAmount: number;
  readonly openSwitchEntityModal?: () => void;
  readonly selectedOrganizationName?: string;
  readonly selectedWorkspaceName?: string;
  readonly shouldShowSubscriptionsLink?: boolean;
}

export function Navbar({
  invitationsAmount,
  openSwitchEntityModal,
  selectedOrganizationName,
  selectedWorkspaceName,
  shouldShowSubscriptionsLink = true,
}: Props) {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isUserProfileModalOpen, setIsUserProfileModalOpen] = useState(false);
  const revalidator = useRevalidator();
  const onLogout = async () => {
    await logout();
    revalidator.revalidate();
  };
  return (
    <NavbarLayout>
      <section className="flex flex-row items-center p-0 gap-[20px] w-full">
        {openSwitchEntityModal ? (
          <Flex justifyContent="start" className="gap-4 w-fit">
            <ChangeEntityButton
              selectedorganization={selectedOrganizationName}
              onClick={openSwitchEntityModal}
              selectedWorkspace={selectedWorkspaceName}
              className="ml-[32px]"
            />
          </Flex>
        ) : (
          <div style={{ flex: '1' }}></div>
        )}
        {invitationsAmount > 0 ? (
          <button
            onClick={() => setIsUserProfileModalOpen(true)}
            className={clsx(
              'relative p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors',
              invitationsAmount ? 'ml-auto' : '',
            )}
          >
            <Mail className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {invitationsAmount}
            </span>
          </button>
        ) : null}
        <div
          className={clsx(
            'flex items-center space-x-4 relative',
            invitationsAmount ? '' : 'ml-auto',
          )}
        >
          {/* Profile Dropdown */}{' '}
          <div className="relative">
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
            >
              {auth.currentUser?.photoURL ? (
                <img
                  className="w-10 h-10 rounded-full"
                  src={auth.currentUser?.photoURL}
                  alt="User profile"
                />
              ) : null}
              <section className="text-left">
                <span className="hidden md:block text-nowrap font-semibold">
                  {auth.currentUser?.displayName}
                </span>
                <span className="hidden md:block text-nowrap text-xs">
                  {auth.currentUser?.email}
                </span>
              </section>
              <ChevronDown className="w-4 h-4" />
            </button>

            {isProfileOpen && (
              <div className="absolute top-full right-0 mt-1 w-50 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                <button
                  onClick={() => setIsUserProfileModalOpen(true)}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                >
                  <User className="w-4 h-4" />
                  <span>Profile</span>
                </button>
                <hr className="my-2" />
                {shouldShowSubscriptionsLink ? (
                  <>
                    <button
                      onClick={async () => {
                        const customerPortalLink =
                          await generateCustomerPortalLinkForUserCloudFunction();
                        window.open(customerPortalLink.data, '_blank');
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <HandCoins className="w-4 h-4" />
                      <span>Manage Subscriptions</span>
                    </button>
                    <hr className="my-2" />
                  </>
                ) : null}
                <button
                  onClick={onLogout}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
        {isProfileOpen && (
          <div
            className="fixed inset-0 z-30"
            onClick={() => setIsProfileOpen(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                setIsProfileOpen(false);
              }
            }}
          />
        )}
      </section>
      <UserProfileModal
        isOpen={isUserProfileModalOpen}
        setClose={() => setIsUserProfileModalOpen(false)}
      />
    </NavbarLayout>
  );
}
